import { MENTION_MARKUP, MENTION_MARKUP_WHOLE } from '../constants/regex';

/**
 * Mention
 *
 * This wrapping class is necessary because the way a mention is
 * represented is different depending on react context vs notifications.
 * We need some intermediary entity to represent a mention that's also
 * easy to read and enforce with pseudo typescript-like entities.
 */
export class Mention extends String {
  /**
   *
   * @param {String} displayText
   * @param {String} modelType
   * @param {String} modelId
   */
  constructor(displayText, modelType, modelId, mentionText) {
    super();
    this.displayText = displayText;
    this.modelType = modelType;
    this.modelId = modelId;
    this.markup = mentionText;
  }

  toString() {
    return `@${this.displayText}`;
  }
}

export const isMentionValid = mention =>
  Boolean(Object.keys(parseMention(mention) || {}).length > 0);

/**
 *
 * @param {String} mentionText
 * @returns {Mention | Object}
 *  the Mention instance (or an empty object for destructuring safety)
 */
export const parseMention = mentionText => {
  if (!mentionText) {
    return {};
  }

  const allowedModelTypes = ['user'];
  const [, displayText, , modelTypeWithId] =
    mentionText.match(MENTION_MARKUP) || [];

  if (!displayText || !modelTypeWithId) {
    return {};
  }

  const [modelType, modelId] = modelTypeWithId.split(':');

  if (!modelType || !modelId || !allowedModelTypes.includes(modelType)) {
    return {};
  }

  return new Mention(displayText, modelType, modelId, mentionText);
};

export const getParsedMentionsFromContent = content => {
  if (!content) {
    return [];
  }

  const mentions = content.match(MENTION_MARKUP, 'gi') || [];

  return mentions
    .map(parseMention)
    .filter(mention => mention instanceof Mention);
};

/**
 * Breaks a comment string into its semantic parts (text vs element).
 * Each part is either a string or a Mention instance.
 * @param {string} content: the entire comment string from the backend
 * @returns {Array<String | Mention>} The ordered list of all semantic parts
 *  of the comment. To be handled by caller.
 */
export const getSemanticCommentParts = content => {
  if (!content?.trim()) {
    return [];
  }

  const mentionRegexp = new RegExp(MENTION_MARKUP_WHOLE, 'gi');
  const contentParts = content.split(mentionRegexp);

  return contentParts.reduce((components, part) => {
    if (part.trim()) {
      if (part.match(MENTION_MARKUP_WHOLE)) {
        components.push(parseMention(part));
      } else {
        components.push(part);
      }
    }

    return components;
  }, []);
};
